import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProviderActivationEventName, addProviderActivationEvent } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { Button, Modal, ModalActions, ModalTitle, useAlert } from '@uplift/ui-v2';

export type SubmitProfileModalProps = {
  onClose: () => void;
  open: boolean;
};

export const SubmitProfileModal: React.VFC<SubmitProfileModalProps> = props => {
  const { classes } = useSubmitProfileModalStyles();
  const me = useCurrentuser();
  const alert = useAlert();
  const queryClient = useQueryClient();
  const submitMutation = useMutation({
    mutationFn: () => addProviderActivationEvent(me.id, ProviderActivationEventName.matchingProfileSubmitted),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'provider', me.id, 'activation' ] });
      props.onClose();
    },
    onError: () => {
      alert.error('Could not submit profile');
    }
  });

  return (
    <Modal open={props.open} onClose={props.onClose} loading={submitMutation.isLoading}>
      <ModalTitle>Ready to submit your profile?</ModalTitle>
      <Box pl={3} pr={2} pb={2}>
        Once you submit, we will review your profile to ensure that it's the best it can be.
        <br /><br />
        We recommend that you don't make any changes to your profile until after we have reviewed it.
      </Box>
      <ModalActions className={classes.modalActions}>
        <Button onClick={props.onClose} disabled={submitMutation.isLoading}>Keep Editing</Button>
        <Button variant="contained" color="secondary" onClick={submitMutation.mutate} disabled={submitMutation.isLoading}>Submit</Button>
      </ModalActions>
    </Modal>
  );
};

const useSubmitProfileModalStyles = makeStyles({ name: 'SubmitProfileModal' })(theme => ({
  modalActions: {
    display: 'flex',
    gap: theme.spacing(1),
    '& > button': {
      flex: 1,
    }
  },
}));
